import { withPermissionGuard } from 'features/ezlogicUserPermissions';

/**
 * Enhances a UI component with required higher-order components (HOCs), such as permission guards.
 *
 * Given a configuration object with a component and optional requirements (e.g., permissions),
 * this function wraps the component with the necessary HOCs and returns the enhanced version.
 *
 * @param {Object} config - Configuration for enhancing the component.
 * @param {React.ComponentType} config.component - The base (raw) React component to enhance.
 * @param {string} [config.requiredPermission] - Permission key required to render this component.
 * @returns {React.ComponentType} The enhanced component, wrapped with all required HOCs.
 *
 * @example
 * import { wrapComponentWithHOCs } from './wrapComponentWithHOCs';
 * import { AddButton } from './AddButton';
 *
 * const config = { component: AddButton, requiredPermission: PERMISSIONS.MANAGE_PLUGINS };
 * const EnhancedAddButton = wrapComponentWithHOCs(config);
 * // <EnhancedAddButton /> will be permission-protected
 */
export function wrapComponentWithHOCs(config) {
    let Comp = config.component;
    if (config.requiredPermission) {
        Comp = withPermissionGuard(config.requiredPermission)(Comp);
    }

    // add other HOCs here as needed
    return Comp;
}
